import { useState } from "react";
import { useAuth } from "@/shared/hooks/useAuth";
import { useBranding } from "@/shared/contexts/BrandingContext";
import {
  User as UserIcon,
  LogOut,
  Settings,
  Users,
  LayoutDashboard,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import type { User } from "@shared/schema/types";
import { logout } from "@/shared/utils/queryClient";

export default function HeaderAuth() {
  const { user, isLoading } = useAuth();
  const { isAegis360 } = useBranding();

  const handleLogin = () => {
    window.location.href = "/login";
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  const typedUser = user as User | null | undefined;

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "U";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!typedUser) return "";
    if (typedUser.firstName && typedUser.lastName) {
      return `${typedUser.firstName} ${typedUser.lastName}`;
    }
    if (typedUser.firstName) return typedUser.firstName;
    if (typedUser.email) return typedUser.email.split("@")[0];
    return "User";
  };

  const isB2BUser = typedUser && typedUser.companyId;

  if (isLoading) {
    return (
      <div className="flex items-center space-x-3">
        <div className="h-9 w-20 bg-gray-200 animate-pulse rounded" />
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className="flex items-center space-x-3"
        data-testid="auth-logged-out"
      >
        <Button
          variant="ghost"
          onClick={handleLogin}
          className={
            isAegis360
              ? "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              : "text-green-600 hover:text-green-700 hover:bg-green-50"
          }
          data-testid="button-login"
        >
          <UserIcon className="h-4 w-4 mr-2" />
          Login
        </Button>
        <Button
          onClick={handleLogin}
          className={
            isAegis360
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-green-600 hover:bg-green-700 text-white"
          }
          data-testid="button-get-started"
        >
          Get Started
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center" data-testid="auth-logged-in">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex items-center space-x-2 hover:bg-gray-100"
            data-testid="button-user-menu"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={typedUser?.profileImageUrl ?? undefined}
                alt={getUserDisplayName()}
              />
              <AvatarFallback
                className={
                  isAegis360
                    ? "bg-blue-100 text-blue-700"
                    : "bg-green-100 text-green-700"
                }
              >
                {getInitials(typedUser?.firstName, typedUser?.lastName)}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:flex flex-col items-start">
              <span
                className="text-sm font-medium text-gray-900"
                data-testid="text-user-name"
              >
                {getUserDisplayName()}
              </span>
              {typedUser?.role && (
                <span
                  className="text-xs text-gray-500 capitalize"
                  data-testid="text-user-role"
                >
                  {typedUser.role.replace("_", " ")}
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-gray-500 hidden md:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {getUserDisplayName()}
              </p>
              <p
                className="text-xs leading-none text-muted-foreground"
                data-testid="text-user-email"
              >
                {typedUser?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <a
              href="/dashboard"
              className="cursor-pointer"
              data-testid="link-dashboard"
            >
              <LayoutDashboard className="mr-2 h-4 w-4" />
              <span>Dashboard</span>
            </a>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <a
              href="/profile"
              className="cursor-pointer"
              data-testid="link-my-account"
            >
              <UserIcon className="mr-2 h-4 w-4" />
              <span>My Account</span>
            </a>
          </DropdownMenuItem>

          {isB2BUser && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a
                  href="/organization/settings"
                  className="cursor-pointer"
                  data-testid="link-company-settings"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Company Settings</span>
                </a>
              </DropdownMenuItem>

              {(typedUser?.isOrgAdmin || typedUser?.canInviteUsers) && (
                <DropdownMenuItem asChild>
                  <a
                    href="/organization/team"
                    className="cursor-pointer"
                    data-testid="link-team-management"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <span>Team Management</span>
                  </a>
                </DropdownMenuItem>
              )}
            </>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={handleLogout}
            className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
            data-testid="button-logout"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Logout</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
